import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';
import { Info } from 'lucide-react';
import MoveDetailModal from './MoveDetailModal';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';


// Intervals adaptés pour les danseurs - plus courts pour maintenir la performance
// Pour les moves individuels : intervalles classiques
const MOVE_REVIEW_INTERVALS = [0, 1, 3, 7, 16, 35, 90];

// Pour les combos : intervalles plus courts pour maintenir la performance
const COMBO_REVIEW_INTERVALS = [0, 1, 2, 4, 8, 15, 30];

function getNextReviewDate(level, isCombo = false) {
  const now = new Date();
  const intervals = isCombo ? COMBO_REVIEW_INTERVALS : MOVE_REVIEW_INTERVALS;
  const days = intervals[Math.min(level, intervals.length - 1)];
  now.setDate(now.getDate() + days);
  return now;
}

function formatDate(date) {
  if (!date) return '-';
  const d = new Date(date);
  return d.toLocaleDateString();
}

const LearningProgressTracker = ({ 
  user, learning, learningProgress,
  onUpdateObjective, onUpdateNotes, onGetStats, onRemoveFromLearning,
  onOpenMoveDetails
}) => {
  const [editingMove, setEditingMove] = useState(null);
  const [editTargetDays, setEditTargetDays] = useState('');
  const [editNotes, setEditNotes] = useState('');
  
  // Moves en apprentissage avec leurs détails et stats
  const learningWithDetails = learning.map(moveId => {
    const move = user?.moves?.find(m => m.id === moveId);
    const stats = onGetStats(moveId);
    return { move, stats, moveId };
  }).filter(item => item.move);
  
  const handleEditObjective = (moveId) => {
    const current = learningProgress[moveId];
    setEditingMove(moveId);
    setEditTargetDays(current?.targetDays || '');
    setEditNotes(current?.notes || '');
  };
  
  const handleSaveObjective = () => {
    if (editingMove) {
      onUpdateObjective(editingMove, editTargetDays ? parseInt(editTargetDays) : null, editNotes);
      setEditingMove(null);
      setEditTargetDays('');
      setEditNotes('');
    }
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'on-track': return 'bg-green-500 text-white';
      case 'warning': return 'bg-yellow-500 text-black';
      case 'overdue': return 'bg-red-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };
  
  const getStatusEmoji = (status) => {
    switch (status) {
      case 'on-track': return '✅';
      case 'warning': return '⚠️';
      case 'overdue': return '🔴';
      default: return '📊';
    }
  };
  
  const formatDuration = (days) => {
    if (days === 0) return 'Started today';
    if (days === 1) return '1 day';
    return `${days} days`;
  };

  return (
    <div className="w-full space-y-6">
      {/* Statistiques globales */}
      <div className="flex justify-center gap-4 mb-6">
        <div className="bg-yellow-500 text-black px-4 py-2 rounded font-bold">
          Currently Learning: {learning.length}
        </div>
        <div className="bg-green-500 text-white px-4 py-2 rounded font-bold">
          On Track: {learningWithDetails.filter(item => item.stats?.status === 'on-track').length}
        </div>
        <div className="bg-red-500 text-white px-4 py-2 rounded font-bold">
          Overdue: {learningWithDetails.filter(item => item.stats?.status === 'overdue').length}
        </div>
      </div>

      {/* Apprentissages en cours */}
      <Card className="bg-gray-900 border-2 border-white">
        <CardHeader>
          <CardTitle className="text-white text-center">
            🎯 Learning Progress Tracker ({learningWithDetails.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {learningWithDetails.length === 0 ? (
            <div className="text-center text-gray-400 py-8">
              You're not currently learning any moves. Add some from the Encyclopedia!
            </div>
          ) : (
            <div className="space-y-4">
              {learningWithDetails.map((item) => (
                <div key={item.moveId} className="bg-black border border-white rounded p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="font-bold text-white text-xl flex items-center justify-between group">
                        <span>{item.move.name}</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0 text-white hover:text-black hover:bg-white transition-all opacity-0 group-hover:opacity-100 ml-2"
                          onClick={() => onOpenMoveDetails(item.move)}
                          title="View move details"
                        >
                          <Info className="w-3 h-3" />
                        </Button>
                      </div>
                      <div className="text-gray-400 text-sm">{item.move.type}</div>
                    </div>
                    
                    {item.stats && (
                      <Badge className={getStatusColor(item.stats.status)}>
                        {getStatusEmoji(item.stats.status)} {item.stats.status}
                      </Badge>
                    )}
                  </div>
                  
                  {item.stats && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div className="bg-gray-800 p-3 rounded">
                        <div className="text-gray-400 text-xs">Time Learning</div>
                        <div className="text-white font-bold">
                          {formatDuration(item.stats.daysElapsed)}
                        </div>
                        <div className="text-gray-400 text-xs">
                          Since {formatDate(item.stats.startDate)}
                        </div>
                      </div>
                      
                      <div className="bg-gray-800 p-3 rounded">
                        <div className="text-gray-400 text-xs">Target</div>
                        <div className="text-white font-bold">
                          {item.stats.targetDays ? `${item.stats.targetDays} days` : 'No target set'}
                        </div>
                        {item.stats.targetDate && (
                          <div className="text-gray-400 text-xs">
                            Due {formatDate(item.stats.targetDate)}
                          </div>
                        )}
                      </div>
                      
                      <div className="bg-gray-800 p-3 rounded">
                        <div className="text-gray-400 text-xs">Progress</div>
                        <div className="text-white font-bold">
                          {item.stats.targetDays ? `${Math.round(item.stats.progress_percentage)}%` : 'Open-ended'}
                        </div>
                        {item.stats.targetDays && (
                          <div className="w-full bg-gray-600 rounded-full h-2 mt-1">
                            <div 
                              className={`h-2 rounded-full ${item.stats.status === 'overdue' ? 'bg-red-500' : item.stats.status === 'warning' ? 'bg-yellow-500' : 'bg-green-500'}`}
                              style={{ width: `${Math.min(item.stats.progress_percentage, 100)}%` }}
                            ></div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {item.stats?.notes && (
                    <div className="bg-gray-800 p-3 rounded mb-4">
                      <div className="text-gray-400 text-xs mb-1">Notes</div>
                      <div className="text-white text-sm">{item.stats.notes}</div>
                    </div>
                  )}
                  
                  {editingMove === item.moveId ? (
                    <div className="bg-gray-800 p-4 rounded space-y-3">
                      <div>
                        <label className="text-white text-sm block mb-1">Learning Objective (days)</label>
                        <input 
                          type="number" 
                          value={editTargetDays}
                          onChange={(e) => setEditTargetDays(e.target.value)}
                          className="bg-black text-white border border-white rounded px-3 py-2 w-full"
                          placeholder="e.g., 14 for 2 weeks"
                        />
                      </div>
                      <div>
                        <label className="text-white text-sm block mb-1">Notes</label>
                        <textarea 
                          value={editNotes}
                          onChange={(e) => setEditNotes(e.target.value)}
                          className="bg-black text-white border border-white rounded px-3 py-2 w-full h-20"
                          placeholder="Learning strategy, focus points, reminders..."
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          onClick={handleSaveObjective}
                          className="bg-green-500 text-white border-2 border-green-600 font-bold px-4 py-2"
                        >
                          💾 Save
                        </Button>
                        <Button 
                          onClick={() => setEditingMove(null)}
                          className="bg-gray-500 text-white border-2 border-gray-600 font-bold px-4 py-2"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex gap-2 flex-wrap">
                      <Button 
                        onClick={() => handleEditObjective(item.moveId)}
                        className="bg-blue-500 text-white border-2 border-blue-600 font-bold px-3 py-1 text-sm"
                      >
                        🎯 Set Objective
                      </Button>
                      <Button 
                        onClick={() => onRemoveFromLearning(item.moveId)}
                        className="bg-red-500 text-white border-2 border-red-600 font-bold px-3 py-1 text-sm"
                      >
                        ❌ Stop Learning
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

    </div>
  );
};

const RevisionView = () => {
  const { 
    user, learning, arsenal, combos, comboRevisions, learningProgress, moveLearningToArsenal,
    updateLearningObjective, updateLearningNotes, getLearningStats, removeFromLearning,
    preferencesLoaded
  } = useAuth();
  const [mode, setMode] = useState('learning'); // 'learning', 'arsenal', 'combos' ou 'progress'
  const [deck, setDeck] = useState([]);
  const [current, setCurrent] = useState(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [movesStatus, setMovesStatus] = useState({});
  const [showTable, setShowTable] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false); // Track if data was loaded from localStorage
  const [selectedMove, setSelectedMove] = useState(null);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [editingNote, setEditingNote] = useState({ moveId: null, content: '', isNew: true });
  const [showObjectiveModal, setShowObjectiveModal] = useState(false);
  const [editingObjective, setEditingObjective] = useState({ moveId: null, targetDays: '', moveName: '' });

  // Texte d'explication selon le mode
  const modeExplanation = mode === 'learning'
    ? "You are reviewing moves you want to learn. When you master a move, it will be moved to your arsenal."
    : mode === 'arsenal'
    ? "You are reviewing moves you already know (your arsenal). This helps you keep your skills fresh and avoid forgetting them."
    : mode === 'combos'
    ? "You are reviewing your custom combos. Practice these combinations to keep them fresh in your memory."
    : "Track your learning progress. See how long you've been learning each move and set time objectives.";

  // Generate human-readable explanation of levels/intervals for dancers
  const getLevelsInfo = (isCombo = false) => {
    const intervals = isCombo ? COMBO_REVIEW_INTERVALS : MOVE_REVIEW_INTERVALS;
    return intervals.map((days, idx) => {
      const label = days === 0 ? 'now' : `${days} day${days > 1 ? 's' : ''}`;
      return `L${idx}: ${label}`;
    }).join('  •  ');
  };

  // Charger la progression depuis l'API
  useEffect(() => {
    const loadRevisionData = async () => {
      if (!user) return;
      
      try {
        const { data } = await api.get('/me/revision-data');
        const savedRevisionData = data.revisionData || {};
        const key = `${user.username || user.id}-${mode}`;
        const saved = savedRevisionData[key] || {};
        
        // Ne pas écraser movesStatus si les données sauvegardées sont vides
        if (Object.keys(saved).length > 0) {
          setMovesStatus(saved);
        }
        setDataLoaded(true);
      } catch (error) {
        console.error('Failed to load revision data:', error);
        setDataLoaded(true);
      }
    };

    loadRevisionData();
  }, [user, mode]);

  // Initialiser movesStatus pour tous les moves du deck courant
  useEffect(() => {
    if (!user?.moves || !dataLoaded || !preferencesLoaded) return;
    
    let items = [];
    if (mode === 'learning') {
      items = user.moves.filter(m => learning.includes(m.id));
    } else if (mode === 'arsenal') {
      items = user.moves.filter(m => arsenal.includes(m.id));
    } else if (mode === 'combos') {
      items = combos; // Tous les combos sont automatiquement disponibles pour la révision
    }
    
    setDeck(items);
    
    // Initialiser movesStatus seulement si pas déjà initialisé
    setMovesStatus(prev => {
      const updated = { ...prev };
      const now = new Date().toISOString();
      
      items.forEach(item => {
        const itemId = item.id;
        
        // Initialiser seulement si pas déjà présent
        if (!updated[itemId]) {
          // Nouveau move : initialiser avec une date de révision immédiate
          updated[itemId] = {
            level: 0,
            lastReview: null,
            nextReview: now
          };
        } else {
          // Move existant : corriger si nécessaire
          const nextReviewDate = new Date(updated[itemId].nextReview);
          const currentTime = new Date();
          
          // Si le niveau est 0 et la date est dans le futur, corriger
          if (updated[itemId].level === 0 && nextReviewDate > currentTime) {
            updated[itemId] = {
              ...updated[itemId],
              nextReview: now,
              lastReview: null
            };
          }
        }
      });
      
      return updated;
    });
  }, [user, learning, arsenal, combos, comboRevisions, mode, dataLoaded, preferencesLoaded]);

  // Sauvegarder la progression
  useEffect(() => {
    const saveRevisionData = async () => {
      if (!user || !dataLoaded || Object.keys(movesStatus).length === 0) return;
      
      try {
        // Récupérer les données existantes
        const { data: existingData } = await api.get('/me/revision-data');
        const existingRevisionData = existingData.revisionData || {};
        
        // Mettre à jour avec les nouvelles données
        const key = `${user.username || user.id}-${mode}`;
        const updatedRevisionData = {
          ...existingRevisionData,
          [key]: movesStatus
        };
        
        // Sauvegarder
        await api.patch('/me/revision-data', { revisionData: updatedRevisionData });
      } catch (error) {
        console.error('Failed to save revision data:', error);
      }
    };

    // Délai pour éviter trop d'appels API
    const timeoutId = setTimeout(saveRevisionData, 1000);
    return () => clearTimeout(timeoutId);
  }, [movesStatus, user, mode, dataLoaded]);

  // Sélectionner un move à réviser (dont nextReview <= aujourd'hui)
  useEffect(() => {
    const today = new Date();
    const dueMoves = deck.filter(m => {
      const status = movesStatus[m.id];
      const nextReviewDate = status ? new Date(status.nextReview) : null;
      return status && nextReviewDate && nextReviewDate <= today;
    });
    
    // Trier les moves à réviser par date de nextReview la plus ancienne (priorité à l'urgence)
    dueMoves.sort((a, b) => {
      const dateA = new Date(movesStatus[a.id]?.nextReview || 0);
      const dateB = new Date(movesStatus[b.id]?.nextReview || 0);
      return dateA - dateB;
    });
    
    if (dueMoves.length > 0) {
      setCurrent(dueMoves[0]);
    } else {
      setCurrent(null);
    }
    setShowAnswer(false);
  }, [deck, movesStatus, mode]);

  // Ajout d'une fonction pour gérer les réponses nuancées
  const handleReview = (ease) => {
    if (!current) return;
    const isCombo = current.contentType === 'combo';
    const intervals = isCombo ? COMBO_REVIEW_INTERVALS : MOVE_REVIEW_INTERVALS;
    
    setMovesStatus(prev => {
      const prevLevel = prev[current.id]?.level || 0;
      let newLevel = prevLevel;
      let nextInterval = 0;
      if (ease === 'again') {
        newLevel = 0;
        nextInterval = 10 / (60 * 24); // 10 minutes en jours
      } else if (ease === 'good') {
        newLevel = Math.min(prevLevel + 1, intervals.length - 2);
        nextInterval = intervals[newLevel];
      } else if (ease === 'easy') {
        newLevel = Math.min(prevLevel + 2, intervals.length - 1);
        nextInterval = intervals[newLevel];
      }
      // Si le niveau max est atteint et qu'on révise learning, on déplace le move dans l'arsenal
      if (mode === 'learning' && newLevel === intervals.length - 1) {
        moveLearningToArsenal(current.id);
      }
      const now = new Date();
      let nextReview = new Date(now);
      if (ease === 'again') {
        nextReview.setMinutes(now.getMinutes() + 10);
      } else {
        nextReview.setDate(now.getDate() + nextInterval);
      }
      return {
        ...prev,
        [current.id]: {
          level: newLevel,
          lastReview: now.toISOString(),
          nextReview: getNextReviewDate(newLevel, isCombo).toISOString()
        }
      };
    });
  };

  // Liste unique des types de moves
  const types = Array.from(new Set((user?.moves || []).map(m => m.type)));

  // Fonction utilitaire pour obtenir l'intervalle en jours pour un niveau donné
  function getIntervalLabel(level, increment = 1, isCombo = false) {
    const intervals = isCombo ? COMBO_REVIEW_INTERVALS : MOVE_REVIEW_INTERVALS;
    const idx = Math.min(level + increment, intervals.length - 1);
    const days = intervals[idx];
    if (days === 0) return 'now';
    if (days === 1) return '1d';
    return days + 'd';
  }

  const findMoveByName = (moveName) => {
    return user?.moves?.find(move => move.name.toLowerCase() === moveName.toLowerCase());
  };

  const handleOpenMoveDetails = (move) => {
    setSelectedMove(move);
  };

  const handleAddNote = (moveId, moveName) => {
    setEditingNote({ moveId, content: '', isNew: true });
    setShowNoteModal(true);
  };

  const handleEditNote = (moveId, content, noteIndex) => {
    setEditingNote({ moveId, content, isNew: false, originalContent: content, noteIndex });
    setShowNoteModal(true);
  };

  const handleSaveNote = () => {
    if (editingNote.content.trim() === '') return;
    
    const currentStats = getLearningStats(editingNote.moveId);
    const timestamp = new Date().toLocaleString();
    
    if (editingNote.isNew) {
      // Ajouter une nouvelle note
      const newNote = `[${timestamp}] ${editingNote.content}`;
      const updatedNotes = currentStats?.notes 
        ? `${currentStats.notes}\n\n${newNote}`
        : newNote;
      updateLearningNotes(editingNote.moveId, updatedNotes);
    } else {
      // Modifier une note existante (garder l'horodatage original)
      if (currentStats.notes) {
        const allNotes = currentStats.notes.split('\n\n');
        
        // Modifier la note spécifique par son index (en tenant compte du reverse)
        const originalIndex = allNotes.length - 1 - editingNote.noteIndex;
        const noteToEdit = allNotes[originalIndex];
        const originalTimestamp = noteToEdit.match(/^\[(.*?)\]/)?.[0] || `[${timestamp}]`;
        const updatedNote = `${originalTimestamp} ${editingNote.content}`;
        
        // Remplacer la note spécifique par la version modifiée
        allNotes[originalIndex] = updatedNote;
        const updatedNotes = allNotes.join('\n\n');
        
        updateLearningNotes(editingNote.moveId, updatedNotes);
      } else {
        const newNote = `[${timestamp}] ${editingNote.content}`;
        updateLearningNotes(editingNote.moveId, newNote);
      }
    }
    
    setShowNoteModal(false);
    setEditingNote({ moveId: null, content: '', isNew: true });
  };

  const handleDeleteNote = (moveId, noteIndex) => {
    
    const currentStats = getLearningStats(moveId);
    if (currentStats.notes) {
      const allNotes = currentStats.notes.split('\n\n');
      
      // Supprimer la note spécifique par son index (en tenant compte du reverse)
      const originalIndex = allNotes.length - 1 - noteIndex;
      allNotes.splice(originalIndex, 1);
      
      const updatedNotes = allNotes.join('\n\n');
      updateLearningNotes(moveId, updatedNotes);
    }
  };

  const handleEditObjective = (moveId, moveName) => {
    const currentStats = getLearningStats(moveId);
    setEditingObjective({ 
      moveId, 
      targetDays: currentStats?.targetDays?.toString() || '', 
      moveName 
    });
    setShowObjectiveModal(true);
  };

  const handleSaveObjective = () => {
    if (editingObjective.targetDays.trim() === '') return;
    
    const days = parseInt(editingObjective.targetDays);
    if (isNaN(days) || days < 0) return;
    
    const currentStats = getLearningStats(editingObjective.moveId);
    updateLearningObjective(editingObjective.moveId, days, currentStats?.notes || '');
    
    setShowObjectiveModal(false);
    setEditingObjective({ moveId: null, targetDays: '', moveName: '' });
  };

  return (
    <div className="min-h-screen bg-black text-white flex flex-col items-center py-10">
      {/* Intro courbe d'Ebbinghaus */}
      <Card className="w-full max-w-2xl border-4 bg-white bg-black mb-8">
        <CardHeader className="border-b-2 bg-white">
          <CardTitle className="text-white font-black text-xl text-center">Spaced Repetition & Ebbinghaus Curve</CardTitle>
        </CardHeader>
        <CardContent className="p-6 flex flex-col items-center">
          <img src="https://upload.wikimedia.org/wikipedia/commons/4/4e/ForgettingCurve.svg" alt="Ebbinghaus Curve" className="mb-4 rounded shadow-lg max-w-xs" />
          <div className="text-gray-200 text-sm text-center max-w-xl">
            <p className="mb-2 font-bold text-white">Why spaced repetition?</p>
            <p className="text-white">
              The Ebbinghaus forgetting curve shows how quickly we forget new information if we don't review it. Spaced repetition helps you review moves just before you're likely to forget them, making your learning much more efficient and long-lasting.
            </p>
            <p className="mt-2 text-white">Each time you mark a move as "I know", the next review is scheduled further in the future. If you forget, the interval resets. This is the most effective way to master and retain your moves!</p>
          </div>
        </CardContent>
      </Card>

      {/* Tableau de suivi */}
      <Button onClick={() => setShowTable(v => !v)} className="mb-6 bg-white text-black border-2 bg-black font-bold px-6 py-2">
        {showTable ? 'Hide Progress Table' : 'Show Progress Table'}
      </Button>
      {showTable && (
        <div className="w-full max-w-3xl mb-8 overflow-x-auto">
          <table className="min-w-full border border-white text-xs">
            <thead>
              <tr className="bg-gray-900 text-white">
                <th className="border border-white p-2">Move</th>
                <th className="border border-white p-2">Level</th>
                <th className="border border-white p-2">Last Review</th>
                <th className="border border-white p-2">Next Review</th>
              </tr>
            </thead>
            <tbody>
              {deck.map(move => (
                <tr key={move.id} className="text-center">
                  <td className="border border-white p-2 font-bold relative group">
                    <div className="flex items-center justify-between">
                      <span>{move.name}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0 text-white hover:text-black hover:bg-white transition-all opacity-0 group-hover:opacity-100 ml-1"
                        onClick={() => handleOpenMoveDetails(move)}
                        title="View move details"
                      >
                        <Info className="w-3 h-3" />
                      </Button>
                    </div>
                  </td>
                  <td className="border border-white p-2">{movesStatus[move.id]?.level ?? 0}</td>
                  <td className="border border-white p-2">{formatDate(movesStatus[move.id]?.lastReview)}</td>
                  <td className="border border-white p-2">{formatDate(movesStatus[move.id]?.nextReview)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Session de révision */}
      <Card className="w-full max-w-xl border-4 bg-white bg-black">
        <CardHeader className="border-b-2 bg-white">
          <CardTitle className="text-white font-black text-2xl text-center">ANKI Revision</CardTitle>
        </CardHeader>
        <CardContent className="p-6 flex flex-col items-center">
          <div className="mb-4 flex gap-2 justify-center">
            <Button onClick={() => setMode('learning')} className={`font-bold px-4 py-2 border-2 ${mode === 'learning' ? 'bg-yellow-500 text-white border-yellow-600' : 'bg-white text-black border-black'}`}>Learning</Button>
            <Button onClick={() => setMode('arsenal')} className={`font-bold px-4 py-2 border-2 ${mode === 'arsenal' ? 'bg-green-600 text-white border-green-700' : 'bg-white text-black border-black'}`}>Arsenal</Button>
            <Button onClick={() => setMode('combos')} className={`font-bold px-4 py-2 border-2 ${mode === 'combos' ? 'bg-purple-600 text-white border-purple-700' : 'bg-white text-black border-black'}`}>Combos</Button>
            <Button onClick={() => setMode('progress')} className={`font-bold px-4 py-2 border-2 ${mode === 'progress' ? 'bg-purple-600 text-white border-purple-700' : 'bg-white text-black border-black'}`}>Progress</Button>
          </div>
          <div className="mb-6 text-center text-white text-sm font-medium max-w-xl space-y-2">
            <p>{modeExplanation}</p>
            <div className="text-gray-300 text-xs text-left bg-gray-800 rounded p-3 mt-2">
              <b>How does revision work?</b><br/>
              <b>Again</b>: You forgot or are unsure. The move will be reviewed again in 10 minutes.<br/>
              <b>Good</b>: You remember it, but it's not automatic yet. The move will be reviewed in <b>a few days</b> (the exact interval is shown on the button and depends on your current level).<br/>
              <b>Easy</b>: You know this move very well. The next review will be even further in the future.<br/>
              <br/>
              The more you use “Good” or “Easy”, the longer the interval becomes. If you want to review a move sooner, use “Again”.<br/>
              <br/>
              <b>Example intervals:</b> {mode === 'combos' ? getLevelsInfo(true) : getLevelsInfo(false)}
            </div>
          </div>
          {mode === 'progress' ? (
            <div className="w-full space-y-6">
              {/* Statistiques globales */}
              <div className="flex justify-center gap-4 mb-6">
                <div className="bg-yellow-500 text-black px-4 py-2 rounded font-bold">
                  Currently Learning: {learning.length}
                </div>
                <div className="bg-green-500 text-white px-4 py-2 rounded font-bold">
                  On Track: {learning.filter(moveId => {
                    const stats = getLearningStats(moveId);
                    return stats?.status === 'on-track';
                  }).length}
                </div>
                <div className="bg-red-500 text-white px-4 py-2 rounded font-bold">
                  Overdue: {learning.filter(moveId => {
                    const stats = getLearningStats(moveId);
                    return stats?.status === 'overdue';
                  }).length}
                </div>
              </div>

              {/* Apprentissages en cours */}
              <Card className="bg-gray-900 border-2 border-white">
                <CardHeader>
                  <CardTitle className="text-white text-center">
                    🎯 Learning Progress Tracker ({learning.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {learning.length === 0 ? (
                    <div className="text-center text-gray-400 py-8">
                      You're not currently learning any moves. Add some from the Encyclopedia!
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {learning.map((moveId) => {
                        const move = user?.moves?.find(m => m.id === moveId);
                        const stats = getLearningStats(moveId);
                        if (!move) return null;
                        
                        return (
                          <div key={moveId} className="bg-black border border-white rounded p-4">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1">
                                <div className="font-bold text-white text-xl flex items-center justify-between group">
                                  <span>{move.name}</span>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-6 w-6 p-0 text-white hover:text-black hover:bg-white transition-all opacity-0 group-hover:opacity-100 ml-2"
                                    onClick={() => handleOpenMoveDetails(move)}
                                    title="View move details"
                                  >
                                    <Info className="w-3 h-3" />
                                  </Button>
                                </div>
                                <div className="text-gray-400 text-sm">{move.type}</div>
                              </div>
                              
                              {stats && (
                                <Badge className={`${
                                  stats.status === 'on-track' ? 'bg-green-500 text-white' :
                                  stats.status === 'warning' ? 'bg-yellow-500 text-black' :
                                  stats.status === 'overdue' ? 'bg-red-500 text-white' :
                                  'bg-gray-500 text-white'
                                }`}>
                                  {stats.status === 'on-track' ? '✅' :
                                   stats.status === 'warning' ? '⚠️' :
                                   stats.status === 'overdue' ? '🔴' : '📊'} {stats.status}
                                </Badge>
                              )}
                            </div>
                            
                            {stats && (
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                <div className="bg-gray-800 p-3 rounded">
                                  <div className="text-gray-400 text-xs">Time Learning</div>
                                  <div className="text-white font-bold">
                                    {stats.daysElapsed === 0 ? 'Started today' :
                                     stats.daysElapsed === 1 ? '1 day' :
                                     `${stats.daysElapsed} days`}
                                  </div>
                                  <div className="text-gray-400 text-xs">
                                    Since {formatDate(stats.startDate)}
                                  </div>
                                </div>
                                
                                <div className="bg-gray-800 p-3 rounded">
                                  <div className="text-gray-400 text-xs">Target</div>
                                  <div className="text-white font-bold">
                                    {stats.targetDays ? `${stats.targetDays} days` : 'No target set'}
                                  </div>
                                  {stats.targetDate && (
                                    <div className="text-gray-400 text-xs">
                                      Due {formatDate(stats.targetDate)}
                                    </div>
                                  )}
                                </div>
                                
                                <div className="bg-gray-800 p-3 rounded">
                                  <div className="text-gray-400 text-xs">Progress</div>
                                  <div className="text-white font-bold">
                                    {stats.targetDays ? `${Math.round(stats.progress_percentage)}%` : 'Open-ended'}
                                  </div>
                                  {stats.targetDays && (
                                    <div className="w-full bg-gray-600 rounded-full h-2 mt-1">
                                      <div 
                                        className={`h-2 rounded-full ${stats.status === 'overdue' ? 'bg-red-500' : stats.status === 'warning' ? 'bg-yellow-500' : 'bg-green-500'}`}
                                        style={{ width: `${Math.min(stats.progress_percentage, 100)}%` }}
                                      ></div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            {stats?.notes && (
                              <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded mb-4 border border-gray-200 dark:border-gray-700">
                                <div className="text-gray-600 dark:text-gray-400 text-xs mb-2 font-bold">📋 Notes History</div>
                                <div className="text-gray-800 dark:text-white text-sm space-y-2 max-h-32 overflow-y-auto">
                                  {stats.notes.split('\n\n').reverse().map((comment, index) => {
                                    if (comment.trim() === '') return null;
                                    const timestampMatch = comment.match(/^\[(.*?)\]/);
                                    const timestamp = timestampMatch ? timestampMatch[1] : '';
                                    const content = timestampMatch ? comment.substring(timestampMatch[0].length).trim() : comment;
                                    
                                    return (
                                      <div key={index} className="bg-white dark:bg-gray-700 p-2 rounded border border-gray-200 dark:border-gray-600">
                                        <div className="text-blue-600 dark:text-blue-300 text-xs font-mono mb-1">{timestamp}</div>
                                        <div className="text-gray-800 dark:text-white text-sm">{content}</div>
                                        <div className="flex gap-2 mt-1">
                                          <button 
                                            onClick={() => handleEditNote(moveId, content, index)}
                                            className="text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 text-xs underline"
                                          >
                                            ✏️ Edit
                                          </button>
                                          <button 
                                            onClick={() => handleDeleteNote(moveId, index)}
                                            className="text-red-600 dark:text-red-400 hover:text-red-500 dark:hover:text-red-300 text-xs underline"
                                          >
                                            🗑️ Delete
                                          </button>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                            
                                                          <div className="flex flex-wrap gap-2">
                                <Button 
                                  onClick={() => handleEditObjective(moveId, move.name)}
                                  className="bg-blue-500 text-white border-2 border-blue-600 font-bold px-3 py-2 text-xs"
                                >
                                  ✏️ Edit Objective
                                </Button>
                                <Button 
                                  onClick={() => handleAddNote(moveId, move.name)}
                                  className="bg-green-500 text-white border-2 border-green-600 font-bold px-3 py-2 text-xs"
                                >
                                  💬 Add note
                                </Button>
                                <Button 
                                  onClick={() => removeFromLearning(moveId)}
                                  className="bg-red-500 text-white border-2 border-red-600 font-bold px-3 py-2 text-xs"
                                >
                                  🗑️ Remove
                                </Button>
                              </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : current ? (
            <div className="w-full flex flex-col items-center">
              <div className="mb-4 w-full">
                <Card className="w-full bg-gray-900 border-2 border-white">
                  <CardHeader>
                    <CardTitle className="text-xl text-white text-center">{current.name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {showAnswer ? (
                      <div className="space-y-3 text-center">
                        <div className="text-sm text-gray-300">{current.description}</div>
                        {current.contentType === 'combo' ? (
                          <div className="mt-4">
                            <div className="text-sm text-gray-400 mb-2">Moves in this combo:</div>
                            <div className="space-y-1">
                              {current.moves.map((move, index) => (
                                <div key={move.uniqueId || move.id} className="flex items-center justify-center gap-2 text-sm">
                                  <span className="text-gray-300">{index + 1}.</span>
                                  <span className="text-white">{move.name}</span>
                                  <Badge className="bg-gray-600 text-white text-xs">{move.type}</Badge>
                                  {move.variations && move.variations.length > 0 && (
                                    <div className="flex gap-1">
                                      {move.variations.map((variation, vIndex) => (
                                        <Badge key={vIndex} className="bg-green-600 text-white text-xs">
                                          {variation}
                                        </Badge>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <>
                            {current.videoLink && (
                              <a href={current.videoLink} target="_blank" rel="noopener noreferrer" className="text-blue-400 underline">Watch video</a>
                            )}
                            <div className="mt-2">
                              <Badge className="bg-white text-black border-black font-bold text-xs">{current.type}</Badge>
                            </div>
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="text-center text-2xl font-black text-white py-8">?</div>
                    )}
                  </CardContent>
                </Card>
              </div>
              {/* Affichage du niveau actuel */}
              <div className="mb-2 text-white text-sm font-bold">Level: {movesStatus[current.id]?.level ?? 0}</div>
              <div className="flex gap-4 mt-4">
                {!showAnswer ? (
                  <Button onClick={() => setShowAnswer(true)} className="bg-white text-black border-2 border-black font-bold px-6 py-2">Show Answer</Button>
                ) : (
                  <>
                    <Button onClick={() => handleReview('again')} className="bg-red-500 text-white border-2 border-black font-bold px-6 py-2">Again<br /><span className='text-xs font-normal'>(10m)</span></Button>
                    <Button onClick={() => handleReview('good')} className="bg-blue-500 text-white border-2 border-black font-bold px-6 py-2">Good<br /><span className='text-xs font-normal'>({current ? getIntervalLabel(movesStatus[current.id]?.level || 0, 1, current.contentType === 'combo') : '-'})</span></Button>
                    <Button onClick={() => handleReview('easy')} className="bg-green-500 text-white border-2 border-black font-bold px-6 py-2">Easy<br /><span className='text-xs font-normal'>({current ? getIntervalLabel(movesStatus[current.id]?.level || 0, 2, current.contentType === 'combo') : '-'})</span></Button>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center text-xl text-green-400 font-bold py-12">
              {mode === 'combos' ? 'All combos reviewed for today! 🎉' : 'All moves reviewed for today! 🎉'}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Move Detail Modal */}
      <MoveDetailModal 
        move={selectedMove}
        isOpen={!!selectedMove}
        onClose={() => setSelectedMove(null)}
      />

      {/* Note Edit Modal */}
      <Dialog open={showNoteModal} onOpenChange={(open) => {
        setShowNoteModal(open);
      }}>
        <DialogContent className="bg-white dark:bg-black border-2 border-black dark:border-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-center">
              {editingNote.isNew ? 'Add New Note' : 'Edit Note'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                {editingNote.isNew ? 'Enter your note:' : 'Edit your note:'}
              </label>
              <textarea
                value={editingNote.content}
                onChange={(e) => setEditingNote(prev => ({ ...prev, content: e.target.value }))}
                className="w-full h-32 p-3 border-2 border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-black dark:text-white resize-none"
                placeholder={editingNote.isNew ? "Enter your learning note here..." : "Edit your note here..."}
                autoFocus
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                onClick={() => {
                  setShowNoteModal(false);
                  setEditingNote({ moveId: null, content: '', isNew: true });
                }}
                className="bg-gray-500 text-white border-2 border-gray-600 font-bold px-4 py-2"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveNote}
                disabled={editingNote.content.trim() === ''}
                className="bg-green-600 text-white border-2 border-green-700 font-bold px-4 py-2 disabled:opacity-50"
              >
                Save Note
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Objective Edit Modal */}
      <Dialog open={showObjectiveModal} onOpenChange={setShowObjectiveModal}>
        <DialogContent className="bg-white dark:bg-black border-2 border-black dark:border-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-center">
              Edit Learning Objective
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">
                Target days for {editingObjective.moveName}:
              </label>
              <input
                type="number"
                value={editingObjective.targetDays}
                onChange={(e) => setEditingObjective(prev => ({ ...prev, targetDays: e.target.value }))}
                className="w-full p-3 border-2 border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-black dark:text-white"
                placeholder="Enter target days..."
                min="0"
                autoFocus
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                onClick={() => {
                  setShowObjectiveModal(false);
                  setEditingObjective({ moveId: null, targetDays: '', moveName: '' });
                }}
                className="bg-gray-500 text-white border-2 border-gray-600 font-bold px-4 py-2"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveObjective}
                disabled={editingObjective.targetDays.trim() === '' || isNaN(parseInt(editingObjective.targetDays)) || parseInt(editingObjective.targetDays) < 0}
                className="bg-blue-600 text-white border-2 border-blue-700 font-bold px-4 py-2 disabled:opacity-50"
              >
                Save Objective
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RevisionView; 